import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const PRESETS = [
  { label: 'Last 1h', hours: 1 },
  { label: 'Last 6h', hours: 6 },
  { label: 'Today', hours: 24 },
  { label: 'Yesterday', hours: 48, offset: 24 },
  { label: 'Last 7d', hours: 168 },
  { label: 'Last 30d', hours: 720 },
];

const DateRangePicker = ({ value, onChange }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const handlePreset = (preset) => {
    const now = Date.now();
    const from = preset.offset
      ? now - preset.hours * 3600000
      : now - preset.hours * 3600000;
    const to = preset.offset
      ? now - preset.offset * 3600000
      : now;
    onChange({ from, to, hours: preset.hours, label: preset.label });
    setShowCustom(false);
  };

  const handleCustomApply = () => {
    if (customFrom && customTo) {
      const from = new Date(customFrom).getTime();
      const to = new Date(customTo).getTime();
      if (from < to) {
        const hours = (to - from) / 3600000;
        onChange({ from, to, hours, label: 'Custom' });
        setShowCustom(false);
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Presets */}
      <div className="flex bg-surface-800 border border-surface-600/30 rounded-lg overflow-hidden">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            onClick={() => handlePreset(preset)}
            className={`px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
              value?.label === preset.label
                ? 'bg-brand-600/20 text-brand-400'
                : 'text-slate-400 hover:text-slate-200 hover:bg-surface-750'
            }`}
          >
            {preset.label}
          </button>
        ))}
        <button
          onClick={() => setShowCustom(!showCustom)}
          className={`px-2.5 py-1.5 text-[11px] font-medium transition-colors flex items-center gap-1 ${
            value?.label === 'Custom'
              ? 'bg-brand-600/20 text-brand-400'
              : 'text-slate-400 hover:text-slate-200 hover:bg-surface-750'
          }`}
        >
          <Calendar className="w-3 h-3" />
          Custom
        </button>
      </div>

      {/* Custom date inputs */}
      {showCustom && (
        <div className="flex items-center gap-2 animate-fade-in">
          <input
            type="datetime-local"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="bg-surface-800 border border-surface-600/30 text-slate-300 text-[11px] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500/50"
          />
          <span className="text-[11px] text-slate-500">to</span>
          <input
            type="datetime-local"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="bg-surface-800 border border-surface-600/30 text-slate-300 text-[11px] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500/50"
          />
          <button
            onClick={handleCustomApply}
            className="px-3 py-1.5 text-[11px] font-medium bg-brand-600/20 text-brand-400 border border-brand-600/20 rounded-lg hover:bg-brand-600/30 transition-colors"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

// Auto-select aggregation based on time range
export const getAggregation = (hours, override = 'auto') => {
  if (override !== 'auto') return override;
  if (hours <= 1) return '10s';
  if (hours <= 6) return '1m';
  if (hours <= 48) return '5m';
  if (hours <= 168) return '1h';
  return '1d';
};

// Aggregation interval in milliseconds
export const getAggregationMs = (agg) => {
  const map = { '10s': 10000, '1m': 60000, '5m': 300000, '1h': 3600000, '1d': 86400000 };
  return map[agg] || 300000;
};

export default DateRangePicker;
